// Shared evidence-key predicates for the Claim Intelligence Package™.
//
// One evidence category = one definition. Every chapter (03, 08, …) that
// asks "does this finding have vendor intelligence / law / regulation /
// carrier / docs / timeline evidence?" must use these helpers so a
// finding cannot be classified differently in two places.
//
// All predicates operate on the finding's `evidence` object keys (lowercased).
// They intentionally do NOT match on narrative prose — a sentence that
// mentions "the statute" is not a citation.

type EvidenceObj = Record<string, unknown> | null | undefined;

function keysOf(ev: EvidenceObj): string[] {
  if (!ev || typeof ev !== "object") return [];
  return Object.keys(ev)
    .filter((k) => {
      const v = (ev as Record<string, unknown>)[k];
      return v != null && v !== "" && !(Array.isArray(v) && v.length === 0);
    })
    .map((k) => k.toLowerCase());
}

const anyKey = (ev: EvidenceObj, re: RegExp) => keysOf(ev).some((k) => re.test(k));

// Vendor Intelligence — real vendor records, not a vendor name.
export const hasVendorEvidence = (ev: EvidenceObj): boolean =>
  anyKey(ev, /vendor_history|vendor_profile|vendor_records|public_record|licens|complaint|enforcement/);

// Law — linked statute / citation record.
export const hasLawEvidence = (ev: EvidenceObj): boolean =>
  anyKey(ev, /^(law|law_reference|statute|statute_reference|citation)$/);

// Regulation — linked regulation / rule record.
export const hasRegulationEvidence = (ev: EvidenceObj): boolean =>
  anyKey(ev, /^(regulation|regulation_reference|cfr|administrative_rule|rule_reference)$/);

// Carrier Guideline — explicit carrier/insurer guideline record.
export const hasCarrierEvidence = (ev: EvidenceObj): boolean =>
  anyKey(ev, /^(carrier|carrier_guideline|insurer_guideline|insurance_guideline)$/);

// Supporting Documentation — a document-specific key, not "any evidence".
export const hasDocumentEvidence = (ev: EvidenceObj): boolean =>
  anyKey(
    ev,
    /^(document|document_url|doc_url|attachment|file|file_url|receipt|invoice_image|contract|uploaded_document)$/,
  );

// Timeline — explicit temporal evidence. Billing duration alone does NOT count.
export const hasTimelineEvidence = (ev: EvidenceObj): boolean =>
  anyKey(ev, /^(timeline|timestamp|start_time|end_time|event_date|activity_log)$/);

// Photo, GPS, Nova, Human — preserved with existing explicit gating.
export const hasPhotoEvidence = (ev: EvidenceObj): boolean =>
  anyKey(ev, /^(photo|photos|photo_url|image|images)$/);

export const hasGpsEvidence = (ev: EvidenceObj): boolean =>
  anyKey(ev, /^(gps|distance|mileage|miles|coordinates)$/);

export const hasNovaEvidence = (ev: EvidenceObj): boolean =>
  anyKey(ev, /^(nova|ai_generated|ai_analysis|nova_analysis)$/);
